import React, { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  Legend,
} from "recharts";
import { Card, Typography } from "antd";
import { ExportToExcel } from "../../../../utils";
const { Text } = Typography;

export const TotalSales = ({ data, dateFilter }) => {
  const salesByDate = data?.map((order) => ({
    fecha: order.dateStart?.toDate().toLocaleDateString(),
    total: order.saleAmount,
  }));

  const salesByDateGrouped = salesByDate?.reduce((acc, curr) => {
    const existing = acc?.find((item) => item.fecha === curr.fecha);
    if (existing) {
      existing.total += curr.total;
      existing.cantidad++;
    } else {
      acc.push({ ...curr, cantidad: 1 });
    }
    return acc;
  }, []);

  salesByDateGrouped?.sort((a, b) => new Date(a.fecha) - new Date(b.fecha));

  const deliveredOrders = dateFilter?.filter(
    (order) => order.status === "Entregado"
  );

  const deliveredSalesByDate = deliveredOrders?.map((order) => ({
    fecha: order.dateStart.toDate().toLocaleDateString(),
    total: order.saleAmount,
  }));

  const deliveredSalesByDateGrouped = deliveredSalesByDate?.reduce(
    (acc, curr) => {
      const existing = acc?.find((item) => item.fecha === curr.fecha);
      if (existing) {
        existing.total += curr.total;
        existing.cantidad++;
      } else {
        acc.push({ ...curr, cantidad: 1 });
      }
      return acc;
    },
    []
  );

  deliveredSalesByDateGrouped?.sort(
    (a, b) => new Date(a.fecha) - new Date(b.fecha)
  );

  const combinedData = salesByDateGrouped?.map((item) => {
    const deliveredItem = deliveredSalesByDateGrouped?.find(
      (i) => i.fecha === item.fecha
    );
    return {
      ...item,
      entregado: deliveredItem ? deliveredItem.total : 0,
    };
  });

  const customTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const totalSales = payload[0].value?.toFixed(2);
      const deliveredSales = payload[1].value?.toFixed(2);
      const cantidadOrdenes = payload[0].payload.cantidad;

      const percentageDeliveredSales = (
        (deliveredSales / totalSales) *
        100
      ).toFixed(2);

      return (
        <div className="custom-tooltip">
          <b>{`${label}`}</b>
          <p>{`Total de ventas: S/ ${totalSales}`}</p>
          <p>{`Total de ventas entregadas: S/ ${deliveredSales}`}</p>
          <p>{`Porcentaje de ventas entregadas: ${percentageDeliveredSales}%`}</p>
          <p>{`Cantidad de órdenes: ${cantidadOrdenes}`}</p>
        </div>
      );
    }

    return null;
  };

  const totalSales = salesByDateGrouped?.reduce(
    (acc, item) => acc + item.total,
    0
  );
  const deliveredSales = deliveredSalesByDateGrouped?.reduce(
    (acc, item) => acc + item.total,
    0
  );
  const percentageDeliveredSales =
    totalSales !== 0 ? (deliveredSales / totalSales) * 100 : 0;
  const totalOrders = deliveredSalesByDateGrouped?.reduce(
    (acc, item) => acc + item.cantidad,
    0
  );

  const dataForExcel = combinedData?.map((item, index) => {
    return {
      Fecha: item.fecha,
      Ventas: `S/ ${item.total.toFixed(2)}`,
      Órdenes: item.cantidad,
      "Órdenes entregadas": `S/ ${item.entregado.toFixed(2)}`,
    };
  });

  const totalsForExcel = {
    Fecha: "Totales",
    "Total de ventas entregadas": `S/ ${deliveredSales?.toFixed(2)}`,
    "Porcentaje de ventas entregadas": `${percentageDeliveredSales?.toFixed(
      2
    )}%`,
    "Total de órdenes": totalOrders,
  };

  dataForExcel?.push(totalsForExcel);

  const COLUMNS = [
    { title: "Fecha", dataIndex: "Fecha", key: "Fecha" },
    { title: "Ventas", dataIndex: "Ventas", key: "Ventas" },
    { title: "Órdenes", dataIndex: "Órdenes", key: "Órdenes" },
    {
      title: "Órdenes entregadas",
      dataIndex: "Órdenes entregadas",
      key: "Órdenes entregadas",
    },
    {
      title: "Total de ventas entregadas",
      dataIndex: "Total de ventas entregadas",
      key: "Total de ventas entregadas",
    },
    {
      title: "Porcentaje de ventas entregadas",
      dataIndex: "Porcentaje de ventas entregadas",
      key: "Porcentaje de ventas entregadas",
    },
    {
      title: "Total de órdenes entregadas",
      dataIndex: "Total de órdenes",
      key: "Total de órdenes",
    },
  ];
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const Chart = () => {
    return (
      <ResponsiveContainer width="100%" height={300}>
        <LineChart
          margin={{ top: 10, right: 40, left: 0, bottom: 0 }}
          data={combinedData}

        >
          <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.5} />
          <XAxis dataKey="fecha" />
          <YAxis />
          <Tooltip content={customTooltip} />
          <Line
            type="natural"
            dataKey="total"
            stroke="#275F6F"
            strokeWidth={2}
            dot={false}
          >
            {windowWidth > 768 && (
              <LabelList
                dataKey="total"
                position="top"
                content={({ x, y, value }) =>
                  value !== 0 ? (
                    <text x={x} y={y - 25} fill="#000" fontWeight="bold" fontSize="12px">
                      {`S/ ${value.toFixed(2)}`}
                    </text>
                  ) : null
                }
              />

            )}
          </Line>
          <Line
            type="monotone"
            dataKey="entregado"
            stroke="#2F8E8D"
            strokeWidth={2}
            dot={false}
          >
            {windowWidth > 768 && (
              <LabelList
                dataKey="entregado"
                position="top"
                content={({ x, y, value }) =>
                  value !== 0 ? (
                    <text x={x} y={y} fill="#000" fontWeight="bold" fontSize="12px">
                      {`S/ ${value.toFixed(2)}`}
                    </text>
                  ) : null
                }
              />

            )}
          </Line>
          <Legend />
        </LineChart>
      </ResponsiveContainer>
    )
  }

  return (
    <Card
      className="w-full"
      title={
        <div
          className="flex justify-between items-center"
        >
          <div>Resumen</div>
          <div>
            <Text>
              Total de ventas entregadas: S/ {deliveredSales?.toFixed(2)}
            </Text>
            <br />
            <Text>
              Porcentaje de ventas entregadas:{" "}
              {percentageDeliveredSales?.toFixed(2)}%
            </Text>
            <br />
            <Text>Total de órdenes: {totalOrders}</Text>
          </div>
        </div>
      }
      bordered={true}
      cover={<Chart />}
    >


      <style jsx>{`
        .custom-tooltip {
          background-color: white;
          border: 1px solid #ccc;
          padding: 10px;
          font-size: 14px;
        }
      `}</style>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: 10,
          marginRight: 20,
        }}
      >
        <ExportToExcel
          data={dataForExcel}
          fileName={"resumen"}
          columns={COLUMNS}
        />
      </div>
    </Card>
  );
};
